import { ApolloCache } from '@apollo/client';
import { GetJournalEntriesQuery, GetJournalEntriesQueryVariables, GetQuestsQuery, GetQuestsQueryVariables } from '../generated/graphql';
import { getJournalEntriesQuery } from '../queries/journalEntries';
import { getQuestsQuery } from '../queries/quest';

/**
 * Iterates through primary GetQuests queries (those with dueDateFrom: null/undefined)
 * These are the initial queries from various pages, not fetchMore queries
 * @param cache Apollo cache instance
 * @param callback Function to execute for each primary cached query (receives variables and cached data)
 */
export function forEachPrimaryQuestsQuery(
    cache: ApolloCache<any>,
    callback: (variables: GetQuestsQueryVariables, cachedData: GetQuestsQuery) => void,
) {
    const cacheData = (cache as any).data.data;
    const rootQueryKeys = Object.keys(cacheData).filter(key => key.startsWith('ROOT_QUERY'));

    rootQueryKeys.forEach(rootKey => {
        const rootQuery = cacheData[rootKey];
        const questKeys = Object.keys(rootQuery).filter(key => key.startsWith('quests('));

        questKeys.forEach(questKey => {
            try {
                // Extract variables from cache key like: quests({"dueDateFrom":"...","dueDateTo":"..."})
                const match = questKey.match(/quests\((.+)\)/);
                if (!match) return;

                const vars = JSON.parse(match[1]) as GetQuestsQueryVariables;

                // Only process primary queries (dueDateFrom is null/undefined)
                // Skip fetchMore queries which have dueDateFrom set
                if (!vars.dueDateFrom || vars.dueDateFrom === null) {
                    const cachedData = cache.readQuery<GetQuestsQuery, GetQuestsQueryVariables>({
                        query: getQuestsQuery,
                        variables: vars,
                    });

                    if (cachedData) {
                        callback(vars, cachedData);
                    }
                }
            } catch (e) {
                // Skip this query if we can't parse it
            }
        });
    });
}

/**
 * Gets user data from any primary cached GetQuests query
 * @param cache Apollo cache instance
 * @returns User data or undefined if no cache exists
 */
export function getUserDataFromCache(cache: ApolloCache<any>): GetQuestsQuery['user'] | undefined {
    let userData: GetQuestsQuery['user'] | undefined;

    forEachPrimaryQuestsQuery(cache, (_, cachedData) => {
        if (!userData && cachedData?.user) {
            userData = cachedData.user;
        }
    });

    return userData;
}

/**
 * Iterates through all cached GetJournalEntries queries
 * @param cache Apollo cache instance
 * @param callback Function to execute for each cached query (receives variables and cached data)
 */
export function forEachJournalEntriesQuery(
    cache: ApolloCache<any>,
    callback: (variables: GetJournalEntriesQueryVariables, cachedData: GetJournalEntriesQuery) => void,
) {
    const cacheData = (cache as any).data.data;
    const rootQueryKeys = Object.keys(cacheData).filter(key => key.startsWith('ROOT_QUERY'));

    rootQueryKeys.forEach(rootKey => {
        const rootQuery = cacheData[rootKey];
        const journalKeys = Object.keys(rootQuery).filter(key => key.startsWith('journalEntries('));

        journalKeys.forEach(journalKey => {
            try {
                // Extract variables from cache key like: journalEntries({"take":10,"orderBy":"descending"})
                const match = journalKey.match(/journalEntries\((.+)\)/);
                if (!match) return;

                const vars = JSON.parse(match[1]) as GetJournalEntriesQueryVariables;

                const cachedData = cache.readQuery<GetJournalEntriesQuery, GetJournalEntriesQueryVariables>({
                    query: getJournalEntriesQuery,
                    variables: vars,
                });

                if (cachedData) {
                    callback(vars, cachedData);
                }
            } catch (e) {
                // Skip this query if we can't parse it
            }
        });
    });
}
